import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  ArrowRightLeft,
  Bot,
  CalendarClock,
  ChevronLeft,
  CircleAlert,
  CircleX,
  FileText,
  Loader2,
  Mail,
  MapPin,
  MessageSquare,
  Navigation,
  Pencil,
  Phone,
  PhoneCall,
  RotateCcw,
  Sparkles,
  UserRound,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import {
  BookSurveyDialog,
  ContactDialog,
  ConvertDialog,
  LostDialog,
} from '@/components/enquiries/action-dialogs'
import { EnquiryStatusBadge } from '@/components/enquiries/views'
import { StatusBadge } from '@/components/quotes/status-badge'
import { FormField, NativeSelect, TextInput } from '@/components/settings/form-kit'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { api } from '@/lib/api'
import {
  assigneesQuery,
  directionsUrl,
  displayName,
  enquiryQuery,
  OPEN_STATUSES,
  REASON_LABEL,
  siteLine,
  SOURCE_LABEL,
  telHref,
  ukDateTime,
  useEnquiryAction,
  type Enquiry,
  type EnquiryDetail,
  type EnquiryEvent,
} from '@/lib/enquiries'
import { ukDate } from '@/lib/quotes'
import { resourceQuery, reportError, type NamedItem } from '@/lib/settings'
import { useTitle } from '@/lib/use-title'

export const Route = createFileRoute('/_app/enquiries/$id')({ component: EnquiryPage })

function Panel({ title, icon, children }: { title: string; icon: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-xl border bg-card">
      <header className="flex items-center gap-2 border-b px-5 py-3.5">
        <span className="text-primary [&_svg]:size-4" aria-hidden>
          {icon}
        </span>
        <h2 className="text-[15px] font-semibold">{title}</h2>
      </header>
      <div className="p-5">{children}</div>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-[13px] text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-[15px] break-words whitespace-pre-line">{children}</dd>
    </div>
  )
}
const none = <span className="text-muted-foreground">Not given</span>

type Dialogs = 'contact' | 'survey' | 'lost' | 'convert' | 'edit' | null

function EnquiryPage() {
  const { id } = Route.useParams()
  const { data, isPending, isError, error } = useQuery(enquiryQuery(id))
  const cats = useQuery(resourceQuery<NamedItem>('price-categories'))
  const [open, setOpen] = useState<Dialogs>(null)
  const act = useEnquiryAction(id)
  useTitle(data ? `${data.reference} ${displayName(data)}` : 'Enquiry')

  if (isError)
    return (
      <p role="alert" className="text-destructive">
        {error.message}
      </p>
    )
  if (isPending) return <Skeleton className="h-96 rounded-xl" />
  const e = data
  const isOpen = OPEN_STATUSES.includes(e.status)
  const catNames = e.categoryIds
    .map((cid) => cats.data?.find((c) => c.id === cid)?.name)
    .filter(Boolean)
  const dialog = (k: Exclude<Dialogs, null>) => ({
    open: open === k,
    onOpenChange: (o: boolean) => setOpen(o ? k : null),
  })

  return (
    <>
      <Link
        to="/enquiries"
        className="mb-3 inline-flex min-h-11 items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground md:min-h-0"
      >
        <ChevronLeft className="size-4" aria-hidden />
        Enquiries
      </Link>
      <header className="mb-5 grid gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="tabular text-sm font-semibold text-muted-foreground">
              {e.reference}
            </span>
            <EnquiryStatusBadge status={e.status} />
          </div>
          <h1 className="mt-1 text-2xl font-semibold break-words md:text-[28px]">
            {displayName(e)}
          </h1>
          <p className="mt-1 flex flex-wrap items-center gap-x-2 text-[15px] text-muted-foreground">
            {e.source === 'kira' && <Bot className="size-4" aria-hidden />}
            {SOURCE_LABEL[e.source]} · {ukDate(e.createdAt, true)}
            {e.createdByName && ` · logged by ${e.createdByName}`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isOpen && (
            <Button className="h-11 md:h-10" onClick={() => setOpen('convert')}>
              <ArrowRightLeft />
              Convert to quote
            </Button>
          )}
          {e.quote && (
            <Button
              className="h-11 md:h-10"
              variant={isOpen ? 'outline' : 'default'}
              render={<Link to="/quotes/$id" params={{ id: e.quote.id }} />}
            >
              <FileText />
              Open quote {e.quote.reference}
            </Button>
          )}
          {isOpen && (
            <Button variant="outline" className="h-11 md:h-10" onClick={() => setOpen('survey')}>
              <CalendarClock />
              {e.surveyAt ? 'Move survey' : 'Book survey'}
            </Button>
          )}
          {(e.status === 'new' || e.status === 'contacted') && (
            <Button variant="outline" className="h-11 md:h-10" onClick={() => setOpen('contact')}>
              <PhoneCall />
              Record contact
            </Button>
          )}
          <Button variant="outline" className="h-11 md:h-10" onClick={() => setOpen('edit')}>
            <Pencil />
            Edit
          </Button>
          {(isOpen || e.status === 'quoted') && (
            <Button variant="ghost" className="h-11 md:h-10" onClick={() => setOpen('lost')}>
              <CircleX />
              Close
            </Button>
          )}
          {(e.status === 'lost' || e.status === 'junk') && !e.quote && (
            <Button
              variant="outline"
              className="h-11 md:h-10"
              disabled={act.isPending}
              onClick={() => act.mutate({ action: 'reopen', success: 'Enquiry reopened' })}
            >
              <RotateCcw />
              Reopen
            </Button>
          )}
        </div>
      </header>

      {e.lostReason && (e.status === 'lost' || e.status === 'junk') && (
        <p className="mb-4 rounded-lg border bg-muted p-3 text-[15px]">
          <span className="font-medium">{e.status === 'junk' ? 'Junk' : 'Lost'}:</span>{' '}
          {e.lostReason}
        </p>
      )}

      {isOpen && e.duplicates.length > 0 && (
        <section
          className="mb-4 rounded-xl border border-warning/40 bg-warning/10 p-4"
          aria-labelledby="dupes-h"
          data-testid="duplicates"
        >
          <h2 id="dupes-h" className="flex items-center gap-2 font-semibold">
            <CircleAlert className="size-4 text-[#7a4d0e] dark:text-warning" aria-hidden />
            This may be an existing customer
          </h2>
          <ul className="mt-2 grid gap-1.5">
            {e.duplicates.map((d) => (
              <li key={d.customerId} className="text-[15px]">
                <Link
                  to="/customers/$id"
                  params={{ id: d.customerId }}
                  className="font-medium text-primary underline-offset-4 hover:underline"
                >
                  {d.customerName}
                </Link>{' '}
                <span className="text-muted-foreground">
                  · {d.reasons.map((r) => REASON_LABEL[r]).join(', ')}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-sm text-muted-foreground">
            When you convert, choose the existing customer so they are not added twice.
          </p>
        </section>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Panel title="Contact" icon={<UserRound />}>
          <dl className="grid gap-4 sm:grid-cols-2">
            <Row label="Name">
              {e.contactName || none}
              {e.jobTitle && <span className="text-muted-foreground"> · {e.jobTitle}</span>}
            </Row>
            <Row label="Business">
              {e.kind === 'domestic' ? 'Home owner' : e.companyName || none}
            </Row>
            <Row label="Phone">
              {e.phone ? (
                <a
                  href={telHref(e.phone)}
                  className="inline-flex min-h-11 items-center gap-1.5 font-medium text-primary md:min-h-0"
                >
                  <Phone className="size-4" aria-hidden />
                  {e.phone}
                </a>
              ) : (
                none
              )}
            </Row>
            <Row label="Email">
              {e.email ? (
                <a
                  href={`mailto:${e.email}`}
                  className="inline-flex min-h-11 items-center gap-1.5 font-medium break-all text-primary md:min-h-0"
                >
                  <Mail className="size-4 shrink-0" aria-hidden />
                  {e.email}
                </a>
              ) : (
                none
              )}
            </Row>
          </dl>
        </Panel>

        <Panel title="Site" icon={<MapPin />}>
          <dl className="grid gap-4">
            <Row label="Address">{siteLine(e) || none}</Row>
            {e.surveyAt && (
              <Row label="Site survey">
                <span className="font-medium">{ukDateTime(e.surveyAt)}</span>
              </Row>
            )}
          </dl>
          {(e.postcode || e.address) && (
            <Button
              variant="outline"
              className="mt-4 h-11 md:h-10"
              render={<a href={directionsUrl(e)} target="_blank" rel="noopener noreferrer" />}
            >
              <Navigation />
              Directions
            </Button>
          )}
        </Panel>

        <Panel title="What they want" icon={<Sparkles />}>
          <dl className="grid gap-4">
            <Row label="Services">{e.services || none}</Row>
            {catNames.length > 0 && (
              <Row label="Categories">
                <span className="flex flex-wrap gap-1.5">
                  {catNames.map((n) => (
                    <span
                      key={n}
                      className="rounded-full border bg-secondary px-2.5 py-0.5 text-sm"
                    >
                      {n}
                    </span>
                  ))}
                </span>
              </Row>
            )}
            {e.notes && <Row label="Notes">{e.notes}</Row>}
          </dl>
        </Panel>

        <Panel title="Who is dealing with it" icon={<UserRound />}>
          <Assign e={e} />
          {e.nextActionAt && (
            <p className="mt-3 text-[15px]">
              <span className="text-muted-foreground">Follow up:</span>{' '}
              <span className="font-medium">{ukDateTime(e.nextActionAt)}</span>
            </p>
          )}
          {e.quote && (
            <p className="mt-3 flex flex-wrap items-center gap-2 text-[15px]">
              <span className="text-muted-foreground">Quote:</span>
              <Link
                to="/quotes/$id"
                params={{ id: e.quote.id }}
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                {e.quote.reference}
              </Link>
              <StatusBadge status={e.quote.status as never} />
            </p>
          )}
        </Panel>

        <div className="lg:col-span-2">
          <Timeline e={e} />
        </div>
      </div>

      <ContactDialog e={e} {...dialog('contact')} />
      <BookSurveyDialog e={e} {...dialog('survey')} />
      <LostDialog e={e} {...dialog('lost')} />
      <ConvertDialog e={e} {...dialog('convert')} />
      <EditDialog e={e} {...dialog('edit')} />
    </>
  )
}

function Assign({ e }: { e: EnquiryDetail }) {
  const qc = useQueryClient()
  const people = useQuery(assigneesQuery)
  const save = useMutation({
    mutationFn: (assignedTo: string | null) =>
      api.patch<Enquiry>(`/enquiries/${e.id}`, { assignedTo, expectedVersion: e.version }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['enquiry', e.id] })
      void qc.invalidateQueries({ queryKey: ['enquiries'] })
      toast.success('Assigned')
    },
    onError: (err) =>
      reportError(err, () => void qc.invalidateQueries({ queryKey: ['enquiry', e.id] })),
  })
  return (
    <FormField label="Assigned to">
      {(p) => (
        <NativeSelect
          {...p}
          value={e.assignedTo ?? ''}
          disabled={save.isPending}
          onChange={(ev) => save.mutate(ev.target.value || null)}
        >
          <option value="">Nobody yet</option>
          {(people.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
          {e.assignedTo && !people.data?.some((s) => s.id === e.assignedTo) && (
            <option value={e.assignedTo}>{e.assignedName}</option>
          )}
        </NativeSelect>
      )}
    </FormField>
  )
}

const EVENT: Record<string, string> = {
  created: 'Enquiry logged',
  note: 'Note',
  contacted: 'Contacted',
  assigned: 'Assigned',
  survey_booked: 'Survey booked',
  survey_moved: 'Survey moved',
  converted: 'Converted to a quote',
  lost: 'Closed as lost',
  junk: 'Closed as junk',
  reopened: 'Reopened',
}

function eventText(v: EnquiryEvent) {
  const d = v.detail
  if (v.type === 'survey_booked' || v.type === 'survey_moved')
    return [ukDateTime(d.surveyAt as string), d.text as string].filter(Boolean).join('. ')
  if (v.type === 'assigned') return `To ${d.to as string}`
  if (v.type === 'converted')
    return `${d.reference as string}, ${d.mode === 'onsite' ? 'on-site survey' : 'off-site'}${d.linkedExisting ? ', existing customer' : ''}`
  if (v.type === 'created') return SOURCE_LABEL[(d.source as keyof typeof SOURCE_LABEL) ?? 'other']
  return (d.text as string) || ''
}

function Timeline({ e }: { e: EnquiryDetail }) {
  const act = useEnquiryAction(e.id)
  const [text, setText] = useState('')
  return (
    <Panel title="Timeline" icon={<MessageSquare />}>
      <form
        className="mb-5 grid gap-2"
        onSubmit={(ev) => {
          ev.preventDefault()
          if (!text.trim()) return
          act.mutate(
            { action: 'notes', body: { text }, success: 'Note added' },
            { onSuccess: () => setText('') },
          )
        }}
      >
        <label htmlFor="enq-note" className="text-sm font-medium">
          Add a note
        </label>
        <Textarea
          id="enq-note"
          rows={2}
          className="text-base md:text-[15px]"
          value={text}
          onChange={(ev) => setText(ev.target.value)}
        />
        <Button
          type="submit"
          variant="outline"
          className="h-11 justify-self-start md:h-10"
          disabled={!text.trim() || act.isPending}
        >
          {act.isPending && <Loader2 className="animate-spin" />}
          Add note
        </Button>
      </form>
      <ol className="grid gap-4">
        {e.events.map((v) => {
          const detail = eventText(v)
          return (
            <li key={v.id} className="flex gap-3">
              <span
                className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-secondary text-secondary-foreground [&_svg]:size-3.5"
                aria-hidden
              >
                {v.actorType === 'agent' ? <Bot /> : <MessageSquare />}
              </span>
              <span className="min-w-0 text-sm">
                <span className="block font-medium">{EVENT[v.type] ?? v.type}</span>
                <span className="block text-muted-foreground">
                  {v.actorLabel ? `${v.actorLabel} · ` : ''}
                  {ukDate(v.at, true)}
                </span>
                {detail && (
                  <span className="mt-1 block rounded-md bg-muted px-2 py-1 whitespace-pre-line">
                    {detail}
                  </span>
                )}
              </span>
            </li>
          )
        })}
      </ol>
    </Panel>
  )
}

function EditDialog({
  e,
  open,
  onOpenChange,
}: {
  e: EnquiryDetail
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const qc = useQueryClient()
  const [f, setF] = useState(e)
  const [seen, setSeen] = useState(open)
  if (open && !seen) {
    setF(e)
    setSeen(true)
  } else if (!open && seen) setSeen(false)
  const set = (patch: Partial<Enquiry>) => setF((x) => ({ ...x, ...patch }))
  const save = useMutation({
    mutationFn: () =>
      api.patch<Enquiry>(`/enquiries/${e.id}`, {
        kind: f.kind,
        companyName: f.companyName,
        contactName: f.contactName,
        jobTitle: f.jobTitle,
        phone: f.phone,
        email: f.email,
        address: f.address,
        town: f.town,
        postcode: f.postcode,
        services: f.services,
        notes: f.notes,
        expectedVersion: e.version,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['enquiry', e.id] })
      void qc.invalidateQueries({ queryKey: ['enquiries'] })
      onOpenChange(false)
      toast.success('Enquiry saved')
    },
    onError: (err) =>
      reportError(err, () => void qc.invalidateQueries({ queryKey: ['enquiry', e.id] })),
  })
  const text = (label: string, key: keyof Enquiry, extra: Record<string, unknown> = {}) => (
    <FormField label={label}>
      {(p) => (
        <TextInput
          {...p}
          {...extra}
          value={(f[key] as string) ?? ''}
          onChange={(ev) => set({ [key]: ev.target.value } as Partial<Enquiry>)}
        />
      )}
    </FormField>
  )
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit enquiry</DialogTitle>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(ev) => {
            ev.preventDefault()
            save.mutate()
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            {text('Business name', 'companyName')}
            {text('Contact name', 'contactName')}
            {text('Job title', 'jobTitle')}
            {text('Phone', 'phone', { type: 'tel', inputMode: 'tel' })}
            <div className="sm:col-span-2">
              {text('Email', 'email', {
                type: 'email',
                inputMode: 'email',
                autoCapitalize: 'none',
              })}
            </div>
            <div className="sm:col-span-2">{text('Building and street', 'address')}</div>
            {text('Town', 'town')}
            {text('Postcode', 'postcode', { autoCapitalize: 'characters' })}
          </div>
          <FormField label="What they want">
            {(p) => (
              <Textarea
                {...p}
                rows={2}
                className="text-base md:text-[15px]"
                value={f.services}
                onChange={(ev) => set({ services: ev.target.value })}
              />
            )}
          </FormField>
          <FormField label="Notes">
            {(p) => (
              <Textarea
                {...p}
                rows={3}
                className="text-base md:text-[15px]"
                value={f.notes}
                onChange={(ev) => set({ notes: ev.target.value })}
              />
            )}
          </FormField>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-11 md:h-10"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" className="h-11 md:h-10" disabled={save.isPending}>
              {save.isPending && <Loader2 className="animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
